import React from 'react';
import {
  Button,
  Popover,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
} from '@mui/material';
import { AddCircle, RestartAlt } from '@mui/icons-material';
import { TitleTotalTabProps } from './tabs/TitleTotalTab';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { BackdropLoading } from '../BackdropLoading';

export interface TabSelectionProps {
  tabs: { [key: string]: TitleTotalTabProps };
  setTabs: (tabs: { [key: string]: TitleTotalTabProps }) => void;
  resetTabs: Function;
  configLoading: boolean;
}

const grid = 8;
const getItemStyle = (isDragging: any, draggableStyle: any) => ({
  ...draggableStyle,
});

export const TabSelection = ({
  tabs,
  setTabs,
  resetTabs,
  configLoading,
}: TabSelectionProps) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onChangeProjectListTabSelection = (tab: any) => {
    setTabs({
      ...tabs,
      [tab.key]: {
        ...tab,
        visible: !tab.visible,
      },
    });
  };

  const onDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }
    let newTabs: { [key: string]: TitleTotalTabProps } = {};
    Object.keys(tabs).forEach(tab => {
      if (tab === result.draggableId) {
        newTabs[tab] = { ...tabs[tab], sequence: result.destination.index };
      } else if (tabs[tab].sequence >= result.destination.index) {
        newTabs[tab] = { ...tabs[tab], sequence: tabs[tab].sequence + 1 };
      } else {
        newTabs[tab] = { ...tabs[tab] };
      }
    });

    setTabs(newTabs);
  };

  return (
    <>
      <Button color="primary" startIcon={<AddCircle />} onClick={handleClick}>
        TABS
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided: any, snapshot: any) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  px: 4,
                  py: 2,
                  position: 'relative',
                }}
              >
                {configLoading && <BackdropLoading absolute />}
                <small>* drag n drop to reorder tabs</small>
                {[...Object.values(tabs)]
                  .sort((a, b) => a?.sequence - b?.sequence)
                  .map((tab: TitleTotalTabProps, index: number) => (
                    <Draggable
                      key={tab.key}
                      draggableId={tab.key}
                      index={index}
                    >
                      {(provided: any, snapshot: any) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={getItemStyle(
                            snapshot.isDragging,
                            provided.draggableProps.style
                          )}
                        >
                          <FormControlLabel
                            key={`project-tab-config-option-${index}`}
                            control={
                              <Checkbox
                                checked={tab.visible}
                                disabled={tab.disabled}
                                color="primary"
                                onChange={() =>
                                  onChangeProjectListTabSelection(tab)
                                }
                              />
                            }
                            label={
                              <span>
                                {tab.title}
                                <small>&nbsp;({tab.total})</small>
                              </span>
                            }
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}
                <Button
                  sx={{ mt: 1 }}
                  color="primary"
                  startIcon={<RestartAlt fontSize="small" />}
                  onClick={async () => {
                    resetTabs();
                  }}
                >
                  <small>Reset Tab Config</small>
                </Button>
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Popover>
    </>
  );
};
